/**
 * Every run this workspace has asked for, and every run something else started
 * on its behalf.
 *
 * **Nothing is filtered out of this list, and the default must stay that way.**
 * A list that hides failed runs reports "no reports" for a workspace whose every
 * run failed — the exact sentence slice 13 exists to stop being told, and the
 * same class of defect as a 0% bounce rate on a workspace that never sent. So
 * `listReportRuns` is called with no status filter, and the four states are told
 * apart by what each row says rather than by which rows are present.
 *
 * **The list is the only copy of a run this screen keeps.** A run created by the
 * panel above is not pushed into local state and reconciled later; the panel
 * says it started one and this list re-reads. Two copies of a record that both
 * claim to be current is how a screen ends up showing a run as `queued` for ever
 * because the update landed in the other one.
 *
 * **It polls, and it stops.** A queued or running row is genuinely going to
 * change without anybody clicking, so the list re-reads on a timer while any run
 * is unfinished — and gives up out loud after {@link MAX_POLL_ROUNDS} rounds, or
 * the moment a read fails, saying so and offering a manual refresh. The rule
 * `use-dataset-status.ts` wrote is the rule here: nothing polls for ever, and a
 * dead API shows as an error rather than as a spinner that never resolves.
 *
 * **A download is fetched, not linked.** The bytes are behind the tenant guard
 * and the storage port deliberately hands out no URL, so `downloadReportRun`
 * fetches them with the access token and `saveBlobAs` gives them to the browser.
 * A failed fetch is reported above the table — an error a person can read, with
 * the request id when the API sent one — rather than as a button that does
 * nothing. The fetch is deliberately **not** aborted when this component
 * unmounts: somebody who clicks Download and then navigates asked for the file,
 * and cancelling it would be this screen overruling them.
 *
 * The rows themselves are in `ReportRunCells.tsx`, which imports no API client
 * so the sentences on them can be rendered and asserted by
 * `scripts/verify-report-screens.tsx`.
 *
 * **Slice 17 shortened three sentences on this panel and weakened none of
 * them.** The description still says failures are listed as failures rather
 * than left out, because that is the promise the whole file exists to keep;
 * what went is the paragraph explaining that a scheduled run appears the same
 * way a manual one does, which the "Requested" column demonstrates on every
 * row. The empty state gained the action it was missing — *ask for one above* —
 * so it now says what the list is, why it is empty, and the one thing that
 * fills it, rather than only the first two. And the download failure still says
 * the report is unaffected, without restating that asking for a file changes
 * nothing, which is the same fact twice.
 */
import {
  DEFAULT_PAGE_SIZE,
  isReportRunFinished,
  reportRunArtifact,
  type ReportRun,
} from '@wend/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Spinner,
  type DataTableColumn,
} from '@wend/ui';
import { RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { errorMessage, isApiError } from '../../lib/api/errors';
import { downloadReportRun, listReportRuns } from '../../lib/api/reports';
import { saveBlobAs } from '../../lib/download';
import { useAsyncData } from '../../lib/use-async-data';
import {
  ReportRunFileCell,
  ReportRunPeriodCell,
  ReportRunProvenanceCell,
  ReportRunStatusCell,
} from './ReportRunCells';

/**
 * Slower than the import poll, because a report is not a progress bar: there are
 * no intermediate counts to watch, only a status that flips once.
 */
const POLL_INTERVAL_MS = 3_000;

/**
 * Rounds before the screen stops checking on its own — about two minutes. A run
 * that is still building after that is not stuck-looking, it is a long report,
 * and a tab left open on it should not re-read for the rest of the afternoon.
 */
const MAX_POLL_ROUNDS = 40;

export interface ReportRunsPanelProps {
  /** Changes when a run is started above, so this list re-reads rather than guessing. */
  refreshKey: number;
}

export function ReportRunsPanel({ refreshKey }: ReportRunsPanelProps) {
  const [page, setPage] = useState(1);
  const [rounds, setRounds] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<unknown>(null);

  const { data, error, status, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) => listReportRuns({ page, pageSize: DEFAULT_PAGE_SIZE }, signal),
    [page, refreshKey],
  );

  const runs = useMemo(() => data?.items ?? [], [data]);
  const unfinished = runs.some((run) => !isReportRunFinished(run));

  // A new page, or a run just started, is a fresh reason to watch.
  useEffect(() => {
    setRounds(0);
  }, [page, refreshKey]);

  useEffect(() => {
    if (!unfinished) return;
    if (error !== null || rounds >= MAX_POLL_ROUNDS) return;
    // Waiting for the in-flight read rather than stacking a second one on top.
    if (status === 'loading') return;

    const timer = window.setTimeout(() => {
      setRounds((value) => value + 1);
      reload();
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [unfinished, error, rounds, status, reload]);

  const stoppedWatching = unfinished && (rounds >= MAX_POLL_ROUNDS || error !== null);

  async function onDownload(run: ReportRun): Promise<void> {
    const artifact = reportRunArtifact(run);
    // Unreachable from the cell, which offers no button without a file. Kept as
    // a guard rather than a `!` so a future caller cannot fetch a run that has
    // nothing to fetch and turn a failed run into a 404 nobody can explain.
    if (artifact === null) return;

    setDownloadError(null);
    setDownloadingId(run.id);
    try {
      const blob = await downloadReportRun(run.id);
      saveBlobAs(blob, artifact.fileName);
    } catch (cause) {
      setDownloadError(cause);
    } finally {
      setDownloadingId(null);
    }
  }

  const columns = useMemo<Array<DataTableColumn<ReportRun>>>(
    () => [
      {
        /* Each cell names the report, then the period it covered (slice 44). */
        key: 'period',
        header: 'Report',
        cell: (run) => <ReportRunPeriodCell run={run} />,
      },
      {
        key: 'status',
        header: 'Status',
        width: '17rem',
        cell: (run) => <ReportRunStatusCell run={run} />,
      },
      {
        key: 'requested',
        header: 'Requested',
        width: '14rem',
        cell: (run) => <ReportRunProvenanceCell run={run} />,
      },
      {
        key: 'file',
        header: 'File',
        width: '11rem',
        cell: (run) => (
          <ReportRunFileCell
            run={run}
            onDownload={(target) => {
              void onDownload(target);
            }}
            downloading={downloadingId === run.id}
          />
        ),
      },
    ],
    [downloadingId],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reports</CardTitle>
        <CardDescription>
          Every run, including the ones that failed — a failed run is listed as a failure with its
          reason, never left out. Each row says what started it.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failedToLoad ? (
          <ErrorState
            error={error}
            title="Could not load this workspace's reports"
            onRetry={reload}
            className="flex flex-col items-start gap-3 py-2"
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              {refreshing && <Spinner size="sm" label="Checking for updates" />}
              {stoppedWatching && (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[0.84rem] leading-snug text-muted">
                    {error === null
                      ? 'A run is still building. This screen has stopped checking on its own.'
                      : 'This screen has stopped checking for updates.'}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    leadingIcon={<RefreshCw aria-hidden="true" className="size-4" />}
                    onClick={() => {
                      setRounds(0);
                      reload();
                    }}
                  >
                    Check again
                  </Button>
                </div>
              )}
            </div>

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The runs below are the last version that loaded.
              </FormAlert>
            )}

            {downloadError !== null && (
              <FormAlert
                tone="error"
                {...(isApiError(downloadError) && downloadError.requestId !== undefined
                  ? { requestId: downloadError.requestId }
                  : {})}
              >
                {errorMessage(downloadError)} The report itself is unaffected.
              </FormAlert>
            )}

            <DataTable
              label="Report runs"
              columns={columns}
              rows={runs}
              getRowKey={(run) => run.id}
              loading={loadingFirst}
              minWidth="60rem"
              empty={
                <p className="max-w-prose text-[0.9rem] text-muted">
                  {pagination !== null && pagination.total > 0
                    ? 'Nothing on this page. There are runs on the earlier ones.'
                    : 'No report has been requested for this workspace yet. Ask for one above — it appears here straight away, before its file exists.'}
                </p>
              }
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
